// <s-recently-viewed> remembers viewed product handles in localStorage and renders up to four other pieces.
const STORAGE_KEY = 'cn-recently-viewed';
const MAX_STORED = 8;
const MAX_SHOWN = 4;

class RecentlyViewed extends HTMLElement {
  #controller = new AbortController();

  connectedCallback() {
    const handle = this.dataset.handle;
    const previous = this.#read().filter((item) => item !== handle);
    this.#write([handle, ...previous].slice(0, MAX_STORED));
    if (previous.length) this.#render(previous.slice(0, MAX_SHOWN));
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  #read() {
    try {
      const value = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      return Array.isArray(value) ? value.filter((item) => typeof item === 'string') : [];
    } catch {
      return [];
    }
  }

  #write(list) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    } catch {
      // Storage can be unavailable in private modes; the section simply stays hidden.
    }
  }

  async #render(handles) {
    const grid = this.querySelector('[data-recent-grid]');
    if (!grid) return;
    const signal = this.#controller.signal;

    // Each piece is rendered server-side by the shared product card (templates/product.card.liquid),
    // so this row matches every other product grid exactly.
    const results = await Promise.all(handles.map(async (handle) => {
      try {
        const response = await fetch(`${this.dataset.productsUrl}${encodeURIComponent(handle)}?view=card`, { signal });
        if (!response.ok) return null;
        const html = (await response.text()).trim();
        // Guard: only accept the bare card view, never a full page.
        return html.includes('group/card') && !/<html|<body/i.test(html) ? html : null;
      } catch {
        return null;
      }
    }));

    const cards = results.filter(Boolean).map((html) => {
      const li = document.createElement('li');
      li.innerHTML = html;
      return li;
    });

    if (!cards.length || signal.aborted) return;
    grid.replaceChildren(...cards);
    this.hidden = false;
    // Let the motion layer stagger the freshly inserted cards like every other grid.
    this.dispatchEvent(new CustomEvent('motion:refresh', { bubbles: true }));
  }
}

if (!customElements.get('s-recently-viewed')) customElements.define('s-recently-viewed', RecentlyViewed);
